import React, { memo, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Input } from "antd";
import { FieldWrapper } from "../FieldWrapper";
import {
  FieldComponentProps,
  TextFieldConfig,
  FieldType,
} from "models/formField";
import { ENTER_KEY_CODE } from "constants/general";
import { getFieldError } from "helpers/formField.components.helper";
import styles from "./TextInputField.module.scss";

/**
 * TextInputField - Text input component integrated with React Hook Form
 * Supports text, email, password, and number types
 */
export const TextInputField: React.FC<FieldComponentProps<TextFieldConfig>> =
  memo(({ config, control: controlProp, errors: errorsProp }) => {
    // Get form context if available
    const formContext = useFormContext();
    const control = controlProp ?? formContext?.control;
    const errors = errorsProp ?? formContext?.formState?.errors;

    const error = getFieldError(errors, config.name);

    const getInputType = useCallback(() => {
      switch (config.type) {
        case FieldType.NUMBER:
          return "number";
        case FieldType.PASSWORD:
          return "password";
        case FieldType.EMAIL:
          return "email";
        default:
          return "text";
      }
    }, [config.type]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>, value: string) => {
        if (e.keyCode === ENTER_KEY_CODE && config.onEnter) {
          config.onEnter(value);
        }
      },
      [config.onEnter],
    );

    if (!control) {
      console.error(
        `TextInputField "${config.name}" requires control prop or FormProvider`,
      );
      return null;
    }

    return (
      <Controller
        name={config.name}
        control={control}
        defaultValue={config.defaultValue ?? ""}
        rules={config.validation}
        render={({ field }) => (
          <FieldWrapper
            label={config.label}
            name={config.name}
            error={error}
            tooltip={config.tooltip}
            required={!!config.validation?.required}
            className={config.className}
            hidden={config.hidden}
          >
            <Input
              {...field}
              id={config.name}
              type={getInputType()}
              placeholder={config.placeholder}
              disabled={config.disabled}
              readOnly={config.readOnly}
              maxLength={config.maxLength}
              autoComplete={config.autoComplete}
              className={`${styles.input} ${error ? styles.inputError : ""}`}
              aria-invalid={!!error}
              aria-describedby={error ? `${config.name}-error` : undefined}
              onKeyDown={(e) => handleKeyDown(e, field.value)}
              onChange={(e) => {
                const value =
                  config.type === FieldType.NUMBER
                    ? e.target.value === ""
                      ? ""
                      : parseFloat(e.target.value)
                    : e.target.value;
                field.onChange(value);
                config.onChange?.(value as string);
              }}
              onBlur={() => {
                field.onBlur();
                config.onBlur?.();
              }}
              {...config.data}
            />
          </FieldWrapper>
        )}
      />
    );
  });

TextInputField.displayName = "TextInputField";
